import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { format, isPast, isToday } from 'date-fns'
import { toast } from 'sonner'
import {
  CheckSquare, AlertTriangle, Calendar, User,
  MoreHorizontal, Trash2, Plus
} from 'lucide-react'
import { activityApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { Activity, ActivityPriority, CRMEntityType } from '@/types'
import { QuickActivityLog } from './QuickActivityLog'

interface TaskListProps {
  entityType?: CRMEntityType
  entityId?: string
  showHeader?: boolean
  className?: string
  filter?: 'all' | 'overdue' | 'today' | 'upcoming'
  showAddButton?: boolean
}

const priorityColors: Record<ActivityPriority, string> = {
  LOW: 'bg-gray-100 text-gray-700',
  MEDIUM: 'bg-blue-100 text-blue-700',
  HIGH: 'bg-orange-100 text-orange-700',
  URGENT: 'bg-red-100 text-red-700',
}

export function TaskList({
  entityType,
  entityId,
  showHeader = true,
  className,
  filter = 'all',
  showAddButton = true,
}: TaskListProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['tasks', filter, entityType, entityId],
    queryFn: async () => {
      if (filter === 'overdue') {
        return { activities: await activityApi.getOverdueTasks(), total: 0 }
      }
      if (filter === 'today') {
        return { activities: await activityApi.getTasksDueToday(), total: 0 }
      }
      return activityApi.getTasks({
        entity_type: entityType as any,
        entity_id: entityId,
        is_completed: filter === 'all' ? undefined : false,
      })
    },
  })

  const completeMutation = useMutation({
    mutationFn: (activityId: string) => activityApi.completeTask(activityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['timeline'] })
      toast.success(t('crm.taskCompleted', 'Task completed'))
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  const reopenMutation = useMutation({
    mutationFn: (activityId: string) => activityApi.reopenTask(activityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['timeline'] })
      toast.success(t('crm.taskReopened', 'Task reopened'))
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  const deleteMutation = useMutation({
    mutationFn: (activityId: string) => activityApi.deleteActivity(activityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['timeline'] })
      toast.success(t('messages.deleteSuccess'))
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.deleteError'))),
  })

  const handleToggleComplete = (task: Activity) => {
    if (task.is_completed) {
      reopenMutation.mutate(task.activity_id)
    } else {
      completeMutation.mutate(task.activity_id)
    }
  }

  if (isLoading) {
    return (
      <Card className={className}>
        {showHeader && (
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5" />
              {t('crm.tasks', 'Tasks')}
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-5 w-5 rounded" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('messages.loadError', 'Failed to load data')}
        </CardContent>
      </Card>
    )
  }

  const tasks = data?.activities || []

  const handleTaskCreated = () => {
    queryClient.invalidateQueries({ queryKey: ['tasks'] })
  }

  return (
    <Card className={className}>
      {showHeader && (
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <CheckSquare className="h-5 w-5" />
            {t('crm.tasks', 'Tasks')}
            {tasks.length > 0 && (
              <Badge variant="secondary" className="ml-1">{tasks.length}</Badge>
            )}
          </CardTitle>
          {showAddButton && entityType && entityId && (
            <QuickActivityLog
              entityType={entityType}
              entityId={entityId}
              onSuccess={handleTaskCreated}
              defaultActivityType="TASK"
              trigger={
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Plus className="h-4 w-4" />
                </Button>
              }
            />
          )}
        </CardHeader>
      )}
      <CardContent>
        {tasks.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-muted-foreground text-sm">{t('crm.noTasks', 'No tasks')}</p>
            {showAddButton && entityType && entityId && (
              <QuickActivityLog
                entityType={entityType}
                entityId={entityId}
                onSuccess={handleTaskCreated}
                defaultActivityType="TASK"
                trigger={
                  <Button variant="outline" size="sm" className="mt-3">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('crm.addTask', 'Add Task')}
                  </Button>
                }
              />
            )}
          </div>
        ) : (
          <div className="space-y-2">{tasks.map((task) => (
            <TaskItem key={task.activity_id} task={task} onToggle={handleToggleComplete} onDelete={(id) => deleteMutation.mutate(id)} />
          ))}</div>
        )}
      </CardContent>
    </Card>
  )
}

interface TaskItemProps {
  task: Activity
  onToggle: (task: Activity) => void
  onDelete: (id: string) => void
}

function TaskItem({ task, onToggle, onDelete }: TaskItemProps) {
  const { t } = useTranslation()
  const isOverdue = task.due_date && isPast(new Date(task.due_date)) && !task.is_completed
  const isDueToday = task.due_date && isToday(new Date(task.due_date))

  return (
    <div className={cn(
      'flex items-start gap-3 p-3 rounded-lg border transition-colors',
      task.is_completed && 'bg-muted/50 opacity-60',
      isOverdue && 'border-red-200 bg-red-50',
      isDueToday && !isOverdue && 'border-orange-200 bg-orange-50'
    )}>
      <Checkbox
        checked={task.is_completed}
        onCheckedChange={() => onToggle(task)}
        className="mt-0.5"
      />

      <div className="flex-1 min-w-0">
        <p className={cn(
          'font-medium text-sm',
          task.is_completed && 'line-through text-muted-foreground'
        )}>
          {task.subject}
        </p>

        <div className="flex flex-wrap items-center gap-2 mt-1">
          {task.due_date && (
            <span className={cn(
              'flex items-center gap-1 text-xs',
              isOverdue ? 'text-red-600' : isDueToday ? 'text-orange-600' : 'text-muted-foreground'
            )}>
              {isOverdue && <AlertTriangle className="h-3 w-3" />}
              <Calendar className="h-3 w-3" />
              {format(new Date(task.due_date), 'MMM d, h:mm a')}
            </span>
          )}

          {task.priority && (
            <Badge variant="secondary" className={cn('text-xs', priorityColors[task.priority])}>
              {task.priority}
            </Badge>
          )}

          {task.assigned_to_name && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <User className="h-3 w-3" />
              {task.assigned_to_name}
            </span>
          )}
        </div>

        {task.description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
            {task.description}
          </p>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onDelete(task.activity_id)} className="text-destructive">
            <Trash2 className="h-4 w-4 mr-2" />
            {t('common.delete', 'Delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export default TaskList

